// ══════════════════════════════════════════════════════════════
// MOTEUR DE RÈGLES NGAP UNIFIÉ — Source de vérité unique
// Reçoit des actes déjà détectés (par les adaptateurs d'entrée) et
// applique TOUTES les règles : cumul 100/50/0, conversion AMX, CIR-9/2025,
// PRADO, majorations, contradictions, limites, risque.
// ══════════════════════════════════════════════════════════════

import type {
  ActeDetecte,
  ActeCalcule,
  MajorationCalculee,
  LimiteCalculee,
  CumulCalcule,
  ContradictionCalculee,
  ForfaitDetecte,
  ResultatUnifie,
  ForceFlags,
} from './types';
import { BASE_AMI, normCode } from './nomenclature';
import { formatAMI } from '@/lib/tarifs';

// ═══ MAJORATIONS ═══
const MAJ_MAU = 1.35;
const MAJ_MCI = 5.00;
const MAJ_MIE = 3.15;
const MAJ_NUIT1 = 9.15;
const MAJ_NUIT2 = 18.30;
const MAJ_DIMANCHE = 8.50;
const MAJ_IFD = 2.75;

function formatMontant(amount: number): string {
  return amount.toFixed(2).replace('.', ',') + ' €';
}

// ───────────────────────────────────────────────
// HELPERS DE DÉTECTION / COHÉRENCE
// ───────────────────────────────────────────────

function isDiabeticEligibleAct(label: string): boolean {
  const l = label.toLowerCase();
  return l.includes('glycém') || l.includes('glycem') || l.includes('dextro') ||
    l.includes('insuline') || l.includes('diabét') || l.includes('diabet');
}

function estDeuxiemeInsulineKw(keyword: string): boolean {
  const k = keyword.toLowerCase();
  return k.includes('deuxième') || k.includes('deuxieme') ||
    k.includes('2e insuline') || k.includes('2ème insuline') || k.includes('2eme insuline') ||
    k.includes('2e ligne') || k.includes('2eme ligne') || k.includes('2e injection') ||
    k.includes('seconde insuline') || k.includes('insuline différente') || k.includes('insuline differente');
}

function estInsulinePremiereKw(keyword: string): boolean {
  return keyword.toLowerCase().includes('insuline') && !estDeuxiemeInsulineKw(keyword);
}

function estGlycemieKw(keyword: string): boolean {
  const k = keyword.toLowerCase();
  return k.includes('glycém') || k.includes('glycem') || k.includes('dextro');
}

function estConstantesLabel(label: string): boolean {
  const l = label.toLowerCase();
  return l.includes('tension artérielle') || l.includes('tension arterielle') ||
    l.includes('pouls') || l.includes('température') || l.includes('temperature') ||
    l.includes('constantes');
}

function isPerfusionSeance(code: string): boolean {
  const c = normCode(code);
  return c === 'AMI 9' || c === 'AMI 10' || c === 'AMI 14' || c === 'AMI 15' || c === 'AMI 6';
}

// Pansement lourd et complexe (AMI 4) — cumulable à taux plein avec une séance de perfusion
function isPansementComplexe(a: ActeCalcule): boolean {
  return a.categorie === 'Pansements' && normCode(a.code) === 'AMI 4';
}

// Organisation de la surveillance d'une perfusion longue (AMI 4, catégorie Perfusions) — CIR-9/2025
function isSurveillancePerfusionLongue(a: ActeCalcule): boolean {
  return normCode(a.code) === 'AMI 4' && a.categorie === 'Perfusions';
}

// ───────────────────────────────────────────────
// DÉTECTION DU FORFAIT BSI À PARTIR DES ACTES
// ───────────────────────────────────────────────
function detecterForfait(actes: ActeCalcule[]): ForfaitDetecte | null {
  for (const a of actes) {
    if (a.code === 'BSC') return { code: 'BSC', label: 'BSC — Dépendance lourde (GIR 1-2)', montantJournalier: 28.70, niveauGIR: 'GIR 1-2' };
    if (a.code === 'BSB') return { code: 'BSB', label: 'BSB — Dépendance intermédiaire (GIR 3)', montantJournalier: 18.20, niveauGIR: 'GIR 3' };
    if (a.code === 'BSA') return { code: 'BSA', label: 'BSA — Dépendance légère (GIR 4)', montantJournalier: 13.00, niveauGIR: 'GIR 4' };
  }
  return null;
}

// ───────────────────────────────────────────────
// FONCTION PRINCIPALE
// ───────────────────────────────────────────────
export function executerCotation(
  actesDetectes: ActeDetecte[],
  flags?: ForceFlags,
  description?: string,
): ResultatUnifie {
  const lower = (description || '').toLowerCase();

  // ─── Étape 0 : aucun acte ───
  if (actesDetectes.length === 0) {
    return {
      actes: [],
      cumuls: [],
      majorations: [],
      limites: [],
      contradictions: [],
      total: { sousTotalActes: 0, totalMajorations: 0, totalGeneral: 0 },
      risque: { niveau: 'Non évaluable', score: 0, couleur: 'gray', details: ['Aucun acte NGAP identifiable.'] },
      sources: ['Aucune source consultée - analyse impossible'],
      avertissements: ['Aucun acte infirmier reconnu. Vérifiez que votre description mentionne des actes cotables selon la NGAP.'],
      resumeAnalyse: 'Aucun acte identifié.',
      actesInjection: 0,
      actesPansement: 0,
      contexte: { hasBSI: false, hasForfait: false, hasDiabete: false, hasPrado: false, forfait: null, ifi: { montantUnitaire: MAJ_IFD, nbVisites: 0, totalIFI: 0 } },
      explications: [],
    };
  }

  // ─── Étape 1 : construction des cotations brutes ───
  let actes: ActeCalcule[] = actesDetectes.map((detected, idx) => {
    const def = detected.definition;
    let code = def.code;
    let coefficient = def.coefficient;
    let label = def.label;

    const labelLower = label.toLowerCase();
    const isIMorSC = labelLower.includes('intramusculaire') || labelLower.includes('sous-cutanée') || labelLower.includes('sous-cutanee');
    const immunodeprime = !!flags?.immunodeprime || /\b(immunodeprime|immunodéprimé)\b/i.test(lower);
    if (immunodeprime && def.categorie === 'Injections' && coefficient === 1 && isIMorSC) {
      coefficient = 1.5;
      code = code.replace('AMI 1', 'AMI 1,5').replace(/\bAMI 1\b(?!,)/, 'AMI 1,5');
      if (!label.includes('immunodéprimé')) label = label + ' (patient immunodéprimé)';
    }

    const fullAmount = Math.round(coefficient * def.baseAmount * 100) / 100;
    return {
      id: `${code}-${idx}`,
      code,
      label,
      coefficient,
      baseAmount: def.baseAmount,
      base: formatMontant(def.baseAmount),
      montant: fullAmount,
      passage: detected.passage,
      periode: detected.periode,
      categorie: def.categorie,
      article: def.article,
      justification: `${label}. ${code} coefficient ${coefficient}.`,
      tauxFacturation: 100,
      derogatoire: !!def.derogatoire,
      article5bis: !!def.article5bis,
      article7: !!def.article7,
      article5ter: !!def.article5ter,
      article6: !!def.article6,
      noMCI: !!def.noMCI,
      jamaisDecote: !!def.jamaisDecote,
      analgesieTopique: !!def.analgesieTopique,
      cumulableAvecAnalgesie: !!def.cumulableAvecAnalgesie,
      inclusDansForfait: false,
      exceptionAppliquee: null,
      regleCumul: null,
      sourceKeyword: detected.sourceKeyword,
    };
  });

  // ─── Étape 2 : contexte détecté (à partir des actes, plus robuste que le texte) ───
  const hasBSI = actes.some((a) => a.categorie === 'Démarche de soins' && a.coefficient > 0);
  const forfait = detecterForfait(actes);
  const hasForfait = forfait !== null;
  const hasBSIContext = hasBSI || hasForfait;
  const hasDiabete = actes.some((a) => a.article5bis || isDiabeticEligibleAct(a.label));
  const hasPrado = actes.some((a) => a.article5ter);
  const hasPostOp = actes.some((a) => a.article7);

  const explications: string[] = [];
  if (hasForfait) explications.push(`Forfait ${forfait!.code} détecté — ${forfait!.label} (${forfait!.montantJournalier.toFixed(2).replace('.', ',')} €/j).`);
  if (hasDiabete) explications.push('Patient diabétique insulino-traité détecté.');
  if (hasPrado) explications.push('Suivi PRADO (programme de retour à domicile) détecté — coté AMI 5,8 (Art. 5ter).');

  // ─── Étape 3 : Absorption dans le forfait BSI ───
  // Les actes marqués inclusDansBSI (nursing, pansement simple, administration de traitement)
  // sont absorbés par le forfait : montant 0, jamais facturés en plus.
  if (hasForfait) {
    actes = actes.map((a) => {
      if (a.categorie !== 'Forfait dépendance' && a.categorie !== 'Démarche de soins' && a.derogatoire === false && a.coefficient > 0) {
        // Vérification via la définition d'origine : inclusDansBSI
        const def = actesDetectes.find((d) => `${d.definition.code}-${actesDetectes.indexOf(d)}` === a.id)?.definition
          || actesDetectes.find((d) => d.definition.label === a.label)?.definition;
        if (def?.inclusDansBSI) {
          return {
            ...a,
            montant: 0,
            tauxFacturation: 0,
            inclusDansForfait: true,
            code: `${a.code} [INCLUS DANS ${forfait!.code}]`,
            justification: `${a.justification} — Inclus dans le forfait ${forfait!.code}. Ne pas facturer séparément.`,
          };
        }
      }
      return a;
    });
  }

  // ─── Étape 3.5 : Fusion glycémie → surveillance diabétique (Art. 5bis) ───
  const fusion = fusionGlycemieSurveillance(actes, hasDiabete);
  actes = fusion.actes;
  explications.push(...fusion.explications);

  // ─── Étape 4 : Règles de cumul 100/50/0 par passage ───
  const passageGroups: Record<string, ActeCalcule[]> = {};
  actes.forEach((c) => {
    const key = c.passage;
    if (!passageGroups[key]) passageGroups[key] = [];
    passageGroups[key].push(c);
  });

  const cumulApplied: ActeCalcule[] = [];

  Object.entries(passageGroups).forEach(([, acts]) => {
    if (acts.length === 0) return;

    const alwaysFull: ActeCalcule[] = [];
    const article5bisActs: ActeCalcule[] = [];
    const article7Acts: ActeCalcule[] = [];
    const perfusionActs: ActeCalcule[] = [];
    const surveillancePerfusionActs: ActeCalcule[] = [];
    const pansementComplexeActs: ActeCalcule[] = [];
    const analgesieTopiqueActs: ActeCalcule[] = [];
    const article10Acts: ActeCalcule[] = [];
    const regularActs: ActeCalcule[] = [];

    const hasPerfusionInPassage = acts.some((a) => isPerfusionSeance(a.code) || isSurveillancePerfusionLongue(a));

    acts.forEach((a) => {
      if (a.categorie === 'Forfait dépendance' || a.categorie === 'Démarche de soins' || a.categorie === 'Télésoin' || a.coefficient === 0) {
        alwaysFull.push(a);
      } else if (a.article.includes('Art. 10')) {
        article10Acts.push(a);
      } else if (a.article7) {
        article7Acts.push(a);
      } else if (hasDiabete && (a.article5bis || isDiabeticEligibleAct(a.label))) {
        article5bisActs.push(a);
      } else if (a.article5bis) {
        article5bisActs.push(a);
      } else if (isPerfusionSeance(a.code)) {
        perfusionActs.push(a);
      } else if (isSurveillancePerfusionLongue(a)) {
        surveillancePerfusionActs.push(a);
      } else if (hasPerfusionInPassage && isPansementComplexe(a)) {
        pansementComplexeActs.push(a);
      } else if (a.analgesieTopique) {
        analgesieTopiqueActs.push(a);
      } else {
        regularActs.push(a);
      }
    });

    // Toujours plein taux
    alwaysFull.forEach((a) => {
      if (a.categorie === 'Télésoin') {
        cumulApplied.push({
          ...a,
          tauxFacturation: 100,
          regleCumul: 'Télésoin — toujours à taux plein (Avenant n°9)',
          justification: `${a.justification} — Avenant n°9 : acte de télésoin toujours à taux plein, jamais décoté.`,
        });
      } else {
        cumulApplied.push({ ...a, tauxFacturation: 100 });
      }
    });

    // Article 7 : tous à 100%
    article7Acts.forEach((a) => {
      cumulApplied.push({ ...a, tauxFacturation: 100, regleCumul: 'Art. 7 — taux plein (immunité 11B)' });
    });

    // Pansement complexe (AMI 4) + perfusion : cumul à taux plein
    pansementComplexeActs.forEach((a) => {
      cumulApplied.push({
        ...a,
        montant: Math.round(a.coefficient * a.baseAmount * 100) / 100,
        tauxFacturation: 100,
        regleCumul: 'Pansement complexe + perfusion — cumul à taux plein',
        justification: `${a.justification} — Le pansement complexe (AMI 4) se cumule à taux plein avec la séance de perfusion (pas de décote Art. 11B).`,
      });
    });

    // Perfusion (AMI 9/10/14/15/6) + pansement complexe : les deux à taux plein.
    // Sans ce correctif, le pansement complexe (compté « prioritaire ») reléguait
    // la perfusion en 2e position → décote à 50%, ce qui contredisait la règle de cumul.
    if (pansementComplexeActs.length > 0) {
      perfusionActs.forEach((a) => {
        cumulApplied.push({
          ...a,
          montant: Math.round(a.coefficient * a.baseAmount * 100) / 100,
          tauxFacturation: 100,
          regleCumul: 'Perfusion + pansement complexe — cumul à taux plein',
          justification: `${a.justification} — Séance de perfusion cumulée à taux plein avec le pansement complexe (AMI 4), pas de décote Art. 11B.`,
        });
      });
      surveillancePerfusionActs.forEach((a) => {
        cumulApplied.push({
          ...a,
          montant: Math.round(a.coefficient * a.baseAmount * 100) / 100,
          tauxFacturation: 100,
          regleCumul: 'Organisation surveillance + pansement complexe — cumul à taux plein',
          justification: `${a.justification} — Organisation de surveillance (AMI 4) cumulée à taux plein avec le pansement complexe (AMI 4), actes techniques distincts.`,
        });
      });
    } else {
      // Pas de pansement complexe : l'organisation de surveillance suit la règle 100/50/0 classique.
      regularActs.push(...surveillancePerfusionActs);
    }

    // Analgésie topique
    const hasPansementCumulable = acts.some((a) => a.cumulableAvecAnalgesie);
    const has5bisActInPassage = hasDiabete && acts.some((a) => a.article5bis);
    analgesieTopiqueActs.forEach((a) => {
      if (hasPansementCumulable) {
        cumulApplied.push({
          ...a,
          tauxFacturation: 100,
          regleCumul: 'Analgésie topique + pansement — cumul à taux plein (dérogation Art. 11B)',
          justification: `${a.justification} — Dérogation Art. 11B : l'analgésie topique (AMI 1,1) se cumule à taux plein avec un pansement d'ulcère ou d'escarre.`,
        });
      } else if (has5bisActInPassage) {
        cumulApplied.push({
          ...a,
          tauxFacturation: 100,
          regleCumul: 'Analgésie topique + acte 5bis — cumul à taux plein (Art. 5bis chapitre II)',
          justification: `${a.justification} — Art. 5bis chapitre II : cumul à taux plein avec la surveillance d'un patient diabétique insulino-traité.`,
        });
      } else {
        regularActs.push(a);
      }
    });

    // Article 10 : un seul acte par séance
    if (article10Acts.length > 0) {
      cumulApplied.push({
        ...article10Acts[0],
        tauxFacturation: 100,
        justification: `${article10Acts[0].justification} — Article 10 : un seul acte de surveillance/administration par séance.`,
      });
    }
    article10Acts.slice(1).forEach((a) => {
      cumulApplied.push({
        ...a,
        montant: 0,
        tauxFacturation: 0,
        regleCumul: 'Art. 10 — non cumulable entre eux',
        justification: `${a.justification} — REFUSÉ : Les actes de surveillance de l'article 10 ne sont pas cumulables entre eux.`,
      });
    });

    // ─── Rapprochement de cohérence Art. 5bis ───
    // Tout acte article5bis détecté dans ce passage doit impérativement
    // figurer dans le groupe diabète. Si un acte 5bis a été laissé dans
    // regularActs (cas de bord rare), on le réintègre dans le groupe.
    const regularActsNot5bis: ActeCalcule[] = [];
    const rescued5bis: ActeCalcule[] = [];
    regularActs.forEach((a) => {
      if (a.article5bis || a.article.includes('5bis') || a.label.toLowerCase().includes('art. 5bis')) {
        rescued5bis.push(a);
      } else {
        regularActsNot5bis.push(a);
      }
    });
    if (rescued5bis.length > 0) {
      article5bisActs.push(...rescued5bis);
    }

    // Classement unifié par coefficient
    const groupeCoef = article5bisActs.reduce((s, a) => s + a.coefficient, 0);
    const perfusionCoef = perfusionActs.reduce((s, a) => s + a.coefficient, 0);

    const rankedEntities: Array<{ coef: number; kind: 'groupe' | 'perfusion' | 'regular'; acts: ActeCalcule[]; jamaisDecote?: boolean }> = [];
    if (article5bisActs.length > 0) rankedEntities.push({ coef: groupeCoef, kind: 'groupe', acts: article5bisActs });
    if (perfusionActs.length > 0 && pansementComplexeActs.length === 0) rankedEntities.push({ coef: perfusionCoef, kind: 'perfusion', acts: perfusionActs });
    regularActsNot5bis.forEach((a) => rankedEntities.push({ coef: a.coefficient, kind: 'regular', acts: [a], jamaisDecote: a.jamaisDecote }));

    rankedEntities.sort((x, y) => y.coef - x.coef);

    const nbActesPrioritaires =
      alwaysFull.filter((a) => a.coefficient > 0).length +
      article7Acts.length +
      pansementComplexeActs.length +
      (pansementComplexeActs.length > 0 ? perfusionActs.length : 0) +
      (pansementComplexeActs.length > 0 ? surveillancePerfusionActs.length : 0) +
      ((hasPansementCumulable || has5bisActInPassage) ? analgesieTopiqueActs.length : 0);

    rankedEntities.forEach((entity, i) => {
      const position = i + nbActesPrioritaires;
      let taux: number;
      if (position === 0) taux = 100;
      else if (position === 1) taux = (entity.kind === 'regular' && entity.jamaisDecote) ? 100 : 50;
      else taux = 0;

      entity.acts.forEach((a) => {
        const adjustedMontant = a.coefficient > 0
          ? Math.round(a.coefficient * a.baseAmount * taux / 100 * 100) / 100
          : 0;

        let justification: string;
        let regleCumul: string | null = null;
        if (entity.kind === 'groupe') {
          if (taux === 100) {
            justification = `${a.justification} — Groupe diabète (Art. 5bis) : coefficient cumulé (${groupeCoef}) le plus élevé → taux plein.`;
            regleCumul = 'Art. 5bis — taux plein (pas de décote 11B entre actes 5bis)';
          } else if (taux === 50) {
            justification = `${a.justification} — Groupe diabète (Art. 5bis) : coefficient cumulé (${groupeCoef}) inférieur → décoté à 50%.`;
            regleCumul = 'Art. 5bis — décoté à 50%';
          } else {
            justification = `${a.justification} — Groupe diabète (Art. 5bis) : relégué en 3e position → gratuit (Art. 11B).`;
            regleCumul = 'Art. 5bis — gratuit (3e position)';
          }
        } else if (entity.kind === 'perfusion') {
          justification = taux === 100
            ? `${a.justification} — Séance de perfusion : cumul à taux plein (exception NGAP Perfusions).`
            : `${a.justification} — Séance de perfusion : décotée à 50% (coefficient inférieur au groupe diabète).`;
          regleCumul = 'Perfusions — cumul à taux plein (courtes + longues + AMI 6)';
        } else if (taux === 100 && entity.jamaisDecote && position === 1) {
          justification = `${a.justification} — 2e acte, mais ponction veineuse directe jamais décotée (dérogation Art. 11B) → taux plein.`;
          regleCumul = 'Ponction veineuse directe — jamais décotée (dérogation Art. 11B)';
        } else if (taux === 50) {
          justification = `${a.justification} — 2e acte de la séance → facturé à 50%.`;
          regleCumul = '2e acte — 50% (Art. 11B)';
        } else if (taux === 0) {
          justification = `${a.justification} — 3e acte et suivants → gratuit (Art. 11B).`;
          regleCumul = '3e+ acte — gratuit (Art. 11B)';
        } else {
          justification = a.justification;
          regleCumul = '1er acte — 100% (Art. 11B)';
        }

        cumulApplied.push({
          ...a,
          montant: adjustedMontant,
          tauxFacturation: taux,
          justification,
          regleCumul,
        });
      });
    });
  });

  // ─── Post-cumul : normalisation stricte des actes 5bis ───
  // Tous les actes article5bis d'un même passage DOIVENT impérativement
  // avoir le même taux (Art. 5bis : "se cote pareil"). Si un acte a
  // été égaré dans regularActs et a un taux différent, on le normalise.
  const taux5bisParPassage: Record<string, number> = {};
  cumulApplied.forEach((a) => {
    if (a.article5bis) {
      const key = a.passage;
      const current = taux5bisParPassage[key];
      if (current === undefined || a.tauxFacturation > current) {
        taux5bisParPassage[key] = a.tauxFacturation;
      }
    }
  });
  actes = cumulApplied.map((a) => {
    if (!a.article5bis) return a;
    const tauxAttendu = taux5bisParPassage[a.passage];
    if (a.tauxFacturation === tauxAttendu) return a;
    const newMontant = a.coefficient > 0
      ? Math.round(a.coefficient * a.baseAmount * tauxAttendu / 100 * 100) / 100
      : 0;
    return {
      ...a,
      montant: newMontant,
      tauxFacturation: tauxAttendu,
      justification: a.justification.replace(/— (Groupe diabète|1er acte|2e acte|3e acte|Séance de perfusion).*/, `— Groupe diabète (Art. 5bis) : normalisé à taux identique ${tauxAttendu}% pour cohérence insuline ↔ surveillance.`),
      regleCumul: `Art. 5bis — taux uniformisé à ${tauxAttendu}%`,
    };
  });

  // actes = normalisation post-cumul (déjà affecté juste au-dessus)

  // ─── Étape 5 : Règles PRADO ───
  actes = appliquerReglesPrado(actes);

  // ─── Étape 6 : Conversion AMX (contexte BSI) ───
  if (hasBSIContext) {
    actes = actes.map((c) => {
      if (c.categorie === 'Forfait dépendance' || c.categorie === 'Démarche de soins' ||
          c.categorie === 'Soins infirmiers' || c.categorie === 'Télésoin' ||
          c.coefficient === 0) return c;

      if (c.article7) {
        return {
          ...c,
          justification: c.justification + ' — Sous BSI : immunité Article 7 conservée (reste en AMI à taux plein).',
        };
      }
      if (c.derogatoire) {
        const fullAmountDerog = Math.round(c.baseAmount * c.coefficient * 100) / 100;
        return {
          ...c,
          code: c.code.replace('AMI', 'AMX'),
          montant: c.coefficient > 0 ? fullAmountDerog : 0,
          tauxFacturation: 100,
          exceptionAppliquee: 'DÉROGATOIRE BSI',
          justification: c.justification.replace('AMI', 'AMX') + ' — Sous BSI : acte dérogatoire en AMX à taux plein.',
        };
      }

      const amxFullAmount = Math.round(c.baseAmount * c.coefficient * 100) / 100;
      const amxHalfAmount = Math.round(amxFullAmount * 0.5 * 100) / 100;
      return {
        ...c,
        code: c.code.replace('AMI', 'AMX'),
        montant: c.tauxFacturation > 0 ? amxHalfAmount : 0,
        tauxFacturation: c.tauxFacturation > 0 ? 50 : 0,
        exceptionAppliquee: 'CONVERSION AMX 50%',
        justification: c.justification.replace('AMI', 'AMX') + ' — Sous BSI : acte en AMX à 50% du coefficient (sauf dérogatoire).',
      };
    });
  }

  const contradictions: ContradictionCalculee[] = [];

  // ─── Étape 7 : Règles CIR-9/2025 ───
  // 7a. AMI 14/15 — une seule cotation longue par jour
  const perfusionsLonguesCot = actes.filter((c) =>
    (normCode(c.code) === 'AMI 14' || normCode(c.code) === 'AMI 15') && c.montant > 0
  );
  if (perfusionsLonguesCot.length > 1) {
    const labelsLongues = perfusionsLonguesCot.map((c) => c.label).join(', ');
    const sorted = [...perfusionsLonguesCot].sort((a, b) => b.coefficient - a.coefficient);
    const gardee = sorted[0];
    const converties = sorted.slice(1);
    actes = actes.map((c) => {
      const estConvertie = converties.some((s) => s.id === c.id);
      if (!estConvertie) return c;
      const montantAMI41 = Math.round(4.1 * BASE_AMI * 100) / 100;
      const code41 = hasBSIContext ? 'AMX 4,1' : 'AMI 4,1';
      return {
        ...c,
        code: code41,
        coefficient: 4.1,
        base: formatMontant(BASE_AMI),
        montant: montantAMI41,
        tauxFacturation: 100,
        justification: `${c.justification} — CIR-9/2025 : une seule perfusion longue par jour. Convertie en ${code41} (${formatAMI(4.1)}).`,
      };
    });
    contradictions.push({
      id: 'ctr-ami1415',
      type: 'warning',
      titre: 'AMI 14/15 — une seule cotation longue par jour (CIR-9/2025)',
      description: `Vous avez sélectionné ${perfusionsLonguesCot.length} perfusions longues (AMI 14/15) : ${labelsLongues}. Une seule cotation par jour et par patient est autorisée. ${gardee.label} (${gardee.code}) conservée, ${converties.length} convertie(s) en AMI 4,1 (${formatAMI(4.1)}).`,
      suggestion: 'Ne facturez qu\'une seule perfusion longue par jour. Les supplémentaires sont cotées en AMI 4,1.',
      severite: 'modere',
      articles: ['CIR-9/2025', 'Art. 11B NGAP', 'NGAP 2026 — Perfusions longues'],
    });
  }

  // 7b. une seule séance de perfusion courte (AMI 9/10) par jour
  const courtesJournee = actes.filter(
    (c) => (normCode(c.code) === 'AMI 9' || normCode(c.code) === 'AMI 10') && c.montant > 0
  );
  if (courtesJournee.length > 1) {
    const orderPassages: Record<string, number> = { 'Matin (08h00)': 0, 'Midi (12h00)': 1, 'Soir (19h00)': 2, matin: 0, midi: 1, soir: 2 };
    const sortedCourtes = [...courtesJournee].sort((a, b) => {
      const diff = (orderPassages[a.passage] ?? orderPassages[a.periode] ?? 0) - (orderPassages[b.passage] ?? orderPassages[b.periode] ?? 0);
      if (diff !== 0) return diff;
      return b.coefficient - a.coefficient;
    });
    const gardee = sortedCourtes[0];
    const converties = sortedCourtes.slice(1);
    actes = actes.map((c) => {
      const estConvertie = converties.some((s) => s.id === c.id);
      if (!estConvertie) return c;
      const montantAMI41 = Math.round(4.1 * BASE_AMI * 100) / 100;
      const code41 = hasBSIContext ? 'AMX 4,1' : 'AMI 4,1';
      return {
        ...c,
        code: code41,
        coefficient: 4.1,
        base: formatMontant(BASE_AMI),
        montant: montantAMI41,
        tauxFacturation: 100,
        justification: `${c.justification} — CIR-9/2025 : une seule séance de perfusion courte par jour. Convertie en ${code41}.`,
      };
    });
    contradictions.push({
      id: 'ctr-perf-courte-journee',
      type: 'warning',
      titre: 'Une seule séance de perfusion courte AMI 9/10 par jour (CIR-9/2025)',
      description: `${courtesJournee.length} perfusions courtes (AMI 9/10) sélectionnées. Une seule séance par jour est facturable. ${converties.length} convertie(s) en AMI 4,1.`,
      suggestion: 'Ne facturez qu\'une seule séance de perfusion courte par jour.',
      severite: 'modere',
      articles: ['CIR-9/2025', 'Art. 11B NGAP'],
    });
  }

  // 7c. AMI 4 interdit le jour de pose (AMI 14/15) ou de retrait (AMI 5)
  const passageGroupsCIR = actes.reduce<Record<string, ActeCalcule[]>>((acc, c) => {
    if (!acc[c.passage]) acc[c.passage] = [];
    acc[c.passage].push(c);
    return acc;
  }, {});
  const actes4Interdits: ActeCalcule[] = [];
  Object.values(passageGroupsCIR).forEach((acts) => {
    const hasAMI1415ouAMI5 = acts.some((c) =>
      (normCode(c.code) === 'AMI 14' || normCode(c.code) === 'AMI 15' || normCode(c.code) === 'AMI 5') && c.montant > 0
    );
    if (!hasAMI1415ouAMI5) return;
    const actes4 = acts.filter((c) => normCode(c.code) === 'AMI 4' && c.categorie === 'Perfusions' && c.montant > 0);
    actes4.forEach((c) => {
      c.montant = 0;
      c.tauxFacturation = 0;
      c.justification = `${c.justification} — CIR-9/2025 : AMI 4 interdit le jour de pose (AMI 14/15) et le jour de retrait (AMI 5). Montant mis à 0 €.`;
    });
    actes4Interdits.push(...actes4);
  });
  if (actes4Interdits.length > 0) {
    contradictions.push({
      id: 'ctr-ami4-pose-retrait',
      type: 'warning',
      titre: 'AMI 4 interdit le jour de pose (AMI 14/15) ou de retrait (AMI 5) (CIR-9/2025)',
      description: 'L\'AMI 4 ne peut être facturé que les jours intermédiaires de surveillance. Montant mis à 0 €.',
      suggestion: 'Retirez l\'AMI 4 du passage de pose ou de retrait.',
      severite: 'modere',
      articles: ['CIR-9/2025 — AMI 4'],
    });
  }

  const hasOnlyAMI4Perfusion = Object.values(passageGroupsCIR).some((acts) => {
    const validActs = acts.filter((c) => c.montant > 0 && c.categorie !== 'Forfait dépendance' && c.categorie !== 'Démarche de soins');
    return validActs.length > 0 && validActs.every((c) => normCode(c.code) === 'AMI 4' && c.categorie === 'Perfusions');
  });

  // 7d. AMI 4,1 interdit lors du passage AMI 14/15
  const actes41Interdits: ActeCalcule[] = [];
  Object.values(passageGroupsCIR).forEach((acts) => {
    const hasAMI1415 = acts.some((c) => (normCode(c.code) === 'AMI 14' || normCode(c.code) === 'AMI 15') && c.montant > 0);
    if (!hasAMI1415) return;
    const actes41 = acts.filter((c) =>
      normCode(c.code) === 'AMI 4,1' && c.montant > 0 &&
      !c.justification.includes('perfusion longue par jour') &&
      !c.justification.includes('perfusion courte par jour')
    );
    actes41.forEach((c) => {
      c.montant = 0;
      c.tauxFacturation = 0;
      c.justification = `${c.justification} — CIR-9/2025 : AMI 4,1 interdit lors du passage facturé AMI 14/15. Montant mis à 0 €.`;
    });
    actes41Interdits.push(...actes41);
  });
  if (actes41Interdits.length > 0) {
    contradictions.push({
      id: 'ctr-ami41-1415',
      type: 'warning',
      titre: 'AMI 4,1 interdit lors du passage facturé en AMI 14/15 (CIR-9/2025)',
      description: 'La facturation d\'un AMI 4,1 est interdite lors du passage où est facturé l\'AMI 14 ou l\'AMI 15. Montant mis à 0 €.',
      suggestion: 'L\'AMI 14/15 couvre déjà les actes associés. L\'AMI 4,1 n\'est facturable que lors d\'un passage séparé.',
      severite: 'modere',
      articles: ['CIR-9/2025', 'Art. 11B NGAP'],
    });
  }

  // 7e. Actes associés AMI 4,1 plafonnés à 2 par passage
  Object.values(passageGroupsCIR).forEach((acts) => {
    const actes41 = acts.filter((c) => normCode(c.code) === 'AMI 4,1' && c.montant > 0);
    if (actes41.length < 2) return;
    const sorted41 = [...actes41].sort((a, b) => b.montant - a.montant);
    const montant41Plein = Math.round(4.1 * BASE_AMI * 100) / 100;
    const montant41Demi = Math.round(4.1 * BASE_AMI * 0.5 * 100) / 100;
    sorted41[0].montant = montant41Plein;
    sorted41[0].tauxFacturation = 100;
    sorted41[1].montant = montant41Demi;
    sorted41[1].tauxFacturation = 50;
    sorted41.slice(2).forEach((c) => { c.montant = 0; c.tauxFacturation = 0; });
  });

  // 7f. AMI 6 — heure supplémentaire, exige une perfusion courte AMI 9/10
  const actes6Invalides: ActeCalcule[] = [];
  Object.values(passageGroupsCIR).forEach((acts) => {
    const actes6 = acts.filter((c) => normCode(c.code) === 'AMI 6' && c.montant > 0);
    if (actes6.length === 0) return;
    const hasAMI9or10 = acts.some((c) => (normCode(c.code) === 'AMI 9' || normCode(c.code) === 'AMI 10') && c.montant > 0);
    if (hasAMI9or10) return;
    actes6.forEach((c) => {
      c.montant = 0;
      c.tauxFacturation = 0;
      c.justification = `${c.justification} — CIR-9/2025 : AMI 6 exige une perfusion courte AMI 9/10 dans le même passage. Montant mis à 0 €.`;
    });
    actes6Invalides.push(...actes6);
  });
  if (actes6Invalides.length > 0) {
    contradictions.push({
      id: 'ctr-ami6-sans-910',
      type: 'warning',
      titre: 'AMI 6 sans perfusion courte AMI 9/10 (CIR-9/2025)',
      description: 'L\'AMI 6 n\'est facturable qu\'en complément d\'un AMI 9 ou AMI 10. Montant mis à 0 €.',
      suggestion: 'Ajoutez la perfusion courte AMI 9/10 dans le même passage, ou retirez l\'AMI 6.',
      severite: 'modere',
      articles: ['CIR-9/2025 — AMI 6'],
    });
  }

  const totalAMI6Jour = actes.filter((c) => normCode(c.code) === 'AMI 6' && c.montant > 0).length;
  if (totalAMI6Jour > 4) {
    contradictions.push({
      id: 'ctr-ami6-limite',
      type: 'warning',
      titre: 'AMI 6 — limite de 5 heures au total dépassée',
      description: `${totalAMI6Jour} AMI 6 facturés dans la journée. Limite de 5 heures au total.`,
      suggestion: 'Réduisez le nombre d\'heures supplémentaires (AMI 6).',
      severite: 'modere',
      articles: ['CIR-9/2025 — AMI 6'],
    });
  }

  // ─── Étape 8 : Totaux ───
  const sousTotalActes = actes.reduce((sum, c) => sum + c.montant, 0);
  const sousTotalActesRounded = Math.round(sousTotalActes * 100) / 100;

  // ─── Étape 9 : Majorations ───
  const allActsAMI1or15 = actes.every((c) => {
    const coef = c.coefficient;
    return (coef === 1 || coef === 1.5) && (c.code.startsWith('AMI') || c.code.startsWith('AMX'));
  });
  const mauApplicable = actes.length === 1 && allActsAMI1or15;

  const hasNuit1 = flags?.nuitProfonde ? false : flags?.nuit !== undefined
    ? flags.nuit
    : (/(nuit|20\s*h|21\s*h|22\s*h|5\s*h|6\s*h|7\s*h)\b/i.test(lower) && !/\b(23\s*h|minuit|0\s*h|1\s*h|2\s*h|3\s*h|4\s*h)\b/i.test(lower));
  const hasNuit2 = flags?.nuitProfonde !== undefined
    ? flags.nuitProfonde
    : /\b(23\s*h|minuit|0\s*h|1\s*h|2\s*h|3\s*h|4\s*h)\b/i.test(lower);
  const hasDimanche = flags?.dimanche || flags?.jourFerie !== undefined
    ? !!(flags?.dimanche || flags?.jourFerie)
    : /\b(dimanche|jour\s*ferie|jour\s*férié|férié|ferie)\b/i.test(lower);

  const nuitActive = hasNuit1 || hasNuit2;
  const dimancheApplicable = hasDimanche && !nuitActive;

  const isSoinComplexe = (c: ActeCalcule): boolean =>
    c.categorie === 'Perfusions' || (c.categorie === 'Pansements' && c.coefficient >= 4 && !c.noMCI);
  const passagesMCI = new Set(actes.filter(isSoinComplexe).map((c) => c.passage));
  if (passagesMCI.size === 0 && /\b(palliatif|coordination)\b/i.test(lower) && actes.length >= 1) {
    passagesMCI.add(actes[0].passage);
  }
  const nbPassagesMCI = passagesMCI.size;
  const mciApplicable = nbPassagesMCI > 0;
  const totalMCI = nbPassagesMCI * MAJ_MCI;

  const hasChildMention = flags?.enfant !== undefined
    ? flags.enfant
    : /\b(enfant|pediatrique|pédiatrique|nourrisson|bebe|bébé|moins de 7)\b/i.test(lower);

  const hasAnyPhysicalActs = actes.some((c) => c.categorie !== 'Télésoin');
  const physicalPassages = actes.filter((c) => c.categorie !== 'Télésoin').map((c) => c.passage);
  const nbPassagesIFD = new Set(physicalPassages).size;
  const totalIFD = nbPassagesIFD * MAJ_IFD;
  const hasAnyActs = actes.length > 0;
  const allActsAreTelesoin = actes.length > 0 && actes.every((c) => c.categorie === 'Télésoin');

  const majorations: MajorationCalculee[] = [
    {
      code: 'IFD',
      nom: 'Indemnité Forfaitaire de Déplacement',
      applicable: hasAnyActs && hasAnyPhysicalActs,
      montant: totalIFD,
      raison: allActsAreTelesoin
        ? 'Non applicable : actes de télésoin — pas de déplacement physique au domicile.'
        : hasAnyPhysicalActs
          ? `Applicable automatiquement : ${nbPassagesIFD} période(s) visitée(s) × IFD ${MAJ_IFD.toFixed(2).replace('.', ',')} € = ${totalIFD.toFixed(2).replace('.', ',')} €.`
          : 'Non applicable : aucun acte de soin détecté.',
      article: 'Art. 13 - NGAP — Indemnités de déplacement',
    },
    {
      code: 'MAU',
      nom: 'Majoration d\'Acte Unique',
      applicable: mauApplicable,
      montant: MAJ_MAU,
      raison: mauApplicable
        ? 'Applicable : un seul acte AMI 1 ou AMI 1,5 isolé (1,35 €).'
        : 'Non applicable.',
      article: 'Avenant 6 - Convention nationale IDEL',
    },
    {
      code: 'MCI',
      nom: 'Majoration de Coordination Infirmière',
      applicable: mciApplicable,
      montant: totalMCI,
      raison: mciApplicable
        ? `Applicable : ${nbPassagesMCI} passage(s) avec soins complexes × ${MAJ_MCI.toFixed(2).replace('.', ',')} € = ${formatMontant(totalMCI)}.`
        : 'Non applicable : pas de soins complexes (pansement lourd, perfusion).',
      article: 'Avenant 6 - Convention nationale IDEL - Coordination',
    },
    {
      code: 'MIE',
      nom: 'Majoration Enfant de moins de 7 ans',
      applicable: hasChildMention,
      montant: MAJ_MIE,
      raison: hasChildMention ? 'Applicable : mention d\'un enfant/pédiatrie (3,15 €).' : 'Non applicable.',
      article: 'Avenant 6 - Convention nationale IDEL',
    },
    {
      code: 'Nuit 20h-23h',
      nom: 'Majoration de nuit (20h-23h / 5h-8h)',
      applicable: hasNuit1,
      montant: MAJ_NUIT1,
      raison: hasNuit1 ? 'Applicable : intervention en horaire de nuit (9,15 €).' : 'Non applicable.',
      article: 'Art. 15 - NGAP - Majorations de nuit',
    },
    {
      code: 'Nuit 23h-5h',
      nom: 'Majoration de nuit profonde (23h-5h)',
      applicable: hasNuit2,
      montant: MAJ_NUIT2,
      raison: hasNuit2 ? 'Applicable : intervention en nuit profonde (18,30 €).' : 'Non applicable.',
      article: 'Art. 15 - NGAP - Majorations de nuit profonde',
    },
    {
      code: 'Dimanche/JF',
      nom: 'Majoration dimanche et jours fériés',
      applicable: dimancheApplicable,
      montant: MAJ_DIMANCHE,
      raison: dimancheApplicable
        ? 'Applicable : intervention un dimanche ou jour férié (8,50 €).'
        : hasDimanche && nuitActive
          ? 'Non applicable : nuit et dimanche/JF ne se cumulent pas.'
          : 'Non applicable.',
      article: 'Art. 14 - NGAP - Majorations dimanches et jours fériés',
    },
  ];

  // PRADO : non cumulable avec nuit/dimanche/JF
  if (hasPrado) {
    for (const m of majorations) {
      if ((m.code === 'Nuit 20h-23h' || m.code === 'Nuit 23h-5h' || m.code === 'Dimanche/JF') && m.applicable) {
        m.applicable = false;
        m.montant = 0;
        m.raison = 'Non applicable : le suivi PRADO (AMI 5,8) n\'est pas cumulable avec les majorations de nuit ou de dimanche/jour férié.';
      }
    }
  }

  const hasOnlyBSIActs = actes.every((c) =>
    c.categorie === 'Forfait dépendance' || c.categorie === 'Démarche de soins'
  );

  const totalMajorations = majorations.reduce((sum, m) => {
    if (!m.applicable) return sum;
    if (hasBSIContext && hasOnlyBSIActs && (m.code === 'MAU' || m.code === 'MCI')) return sum;
    return sum + m.montant;
  }, 0);

  // ─── Étape 10 : Cumuls (liste de vérification) ───
  const cumuls: CumulCalcule[] = [];
  const article5bisCodeSet = new Set(
    actes.filter((c) => (hasDiabete && (c.article5bis || isDiabeticEligibleAct(c.label))) || (!hasDiabete && c.article5bis))
      .map((c) => `${c.code}|${c.categorie}`)
  );
  const ponctionVeineuseCodes = new Set(actes.filter((c) => c.jamaisDecote).map((c) => c.code));
  const analgesieCodes = new Set(actes.filter((c) => c.analgesieTopique).map((c) => c.code));
  const pansementCumulableCodes = new Set(actes.filter((c) => c.cumulableAvecAnalgesie).map((c) => c.code));
  const article7CodeSet = new Set(actes.filter((c) => c.article7).map((c) => `${c.code}|${c.categorie}`));

  const codesUniques = [...new Set(actes.map((c) => `${c.code}|${c.categorie}`))];
  for (let i = 0; i < codesUniques.length; i++) {
    for (let j = i + 1; j < codesUniques.length; j++) {
      const [codeA, catA] = codesUniques[i].split('|');
      const [codeB, catB] = codesUniques[j].split('|');
      const bothArt7 = article7CodeSet.has(`${codeA}|${catA}`) && article7CodeSet.has(`${codeB}|${catB}`);
      const both5bis = article5bisCodeSet.has(`${codeA}|${catA}`) && article5bisCodeSet.has(`${codeB}|${catB}`);
      const bothPerfusionSeances = isPerfusionSeance(codeA) && isPerfusionSeance(codeB);
      const ponctionVeineusePair = ponctionVeineuseCodes.has(codeA) || ponctionVeineuseCodes.has(codeB);
      const analgesiePansementPair =
        (analgesieCodes.has(codeA) && pansementCumulableCodes.has(codeB)) ||
        (analgesieCodes.has(codeB) && pansementCumulableCodes.has(codeA));
      const analgesie5bisPair = hasDiabete && (
        (analgesieCodes.has(codeA) && article5bisCodeSet.has(`${codeB}|${catB}`)) ||
        (analgesieCodes.has(codeB) && article5bisCodeSet.has(`${codeA}|${catA}`))
      );
      const pansementComplexePerfusionPair =
        ((catA === 'Pansements' && normCode(codeA) === 'AMI 4') && (isPerfusionSeance(codeB) || (normCode(codeB) === 'AMI 4' && catB === 'Perfusions'))) ||
        ((catB === 'Pansements' && normCode(codeB) === 'AMI 4') && (isPerfusionSeance(codeA) || (normCode(codeA) === 'AMI 4' && catA === 'Perfusions')));
      const sameCategoryConflict = catA === catB && ['Pansements', 'Perfusions'].includes(catA);
      const compatible = bothArt7 || both5bis || bothPerfusionSeances || ponctionVeineusePair || analgesiePansementPair || analgesie5bisPair || pansementComplexePerfusionPair || !sameCategoryConflict;

      let regle: string;
      let article: string;
      if (bothArt7) { regle = 'Cumul à taux plein autorisé : actes postopératoires Art. 7.'; article = 'Art. 7 - NGAP'; }
      else if (both5bis) { regle = 'Cumul à taux plein autorisé : Article 5bis — exception diabétique insulino-traité.'; article = 'Art. 5bis - NGAP'; }
      else if (bothPerfusionSeances) { regle = 'Cumul à taux plein autorisé : séances de perfusion.'; article = 'NGAP — Perfusions'; }
      else if (ponctionVeineusePair) { regle = 'Cumul à taux plein autorisé : ponction veineuse directe jamais décotée.'; article = 'Art. 11B - NGAP — Dérogation ponction veineuse'; }
      else if (analgesiePansementPair) { regle = 'Cumul à taux plein autorisé : analgésie topique + pansement d\'ulcère/escarre.'; article = 'Art. 11B - NGAP — Dérogation analgésie topique'; }
      else if (analgesie5bisPair) { regle = 'Cumul à taux plein autorisé : analgésie topique + acte 5bis.'; article = 'Art. 5bis - NGAP'; }
      else if (pansementComplexePerfusionPair) { regle = 'Cumul à taux plein autorisé : pansement complexe (AMI 4) + séance de perfusion.'; article = 'NGAP — Pansement complexe + perfusion'; }
      else if (compatible) { regle = 'Cumul autorisé : actes de nature différente ou complémentaires.'; article = 'Art. 11B - NGAP'; }
      else { regle = 'Cumul à vérifier : deux actes de même nature peuvent faire double emploi.'; article = 'Art. 11B - NGAP — Vérifier'; }

      cumuls.push({ acte1: `${codeA} (${catA})`, acte2: `${codeB} (${catB})`, compatible, regle, article });
    }
  }

  if (cumuls.length === 0 && actes.length >= 2) {
    const catNames = [...new Set(actes.map((c) => c.categorie))];
    for (let i = 0; i < catNames.length; i++) {
      for (let j = i + 1; j < catNames.length; j++) {
        cumuls.push({ acte1: catNames[i], acte2: catNames[j], compatible: true, regle: 'Cumul autorisé : catégories de soins distinctes.', article: 'Art. 11B - NGAP' });
      }
    }
  }
  if (cumuls.length === 0) {
    cumuls.push({ acte1: 'Acte unique', acte2: '—', compatible: true, regle: 'Un seul type d\'acte identifié.', article: '—' });
  }

  // ─── Étape 11 : Limites ───
  const limites: LimiteCalculee[] = [];
  const actesInjection = actes.filter((c) => c.categorie === 'Injections').length;
  if (actesInjection > 0) {
    limites.push({ code: 'Inj.', nom: 'Injections', limite: '3 passages / jour max', actuel: `${actesInjection} passage${actesInjection > 1 ? 's' : ''}`, ok: actesInjection <= 3, article: 'Art. 11B - NGAP' });
  }
  const actesPansement = actes.filter((c) => c.categorie === 'Pansements').length;
  if (actesPansement > 0) {
    limites.push({ code: 'Pans.', nom: 'Pansements', limite: '1 / jour (sauf prescription)', actuel: `${actesPansement} pansement${actesPansement > 1 ? 's' : ''}`, ok: actesPansement <= 1, article: 'Art. 11B - NGAP' });
  }
  if (actes.some((c) => c.article6)) {
    const dialyseCount = actes.filter((c) => c.categorie === 'Dialyse').length;
    limites.push({ code: 'Dial.', nom: 'Dialyse péritonéale', limite: '4 séances / jour max', actuel: `${dialyseCount || 1} séance`, ok: (dialyseCount || 1) <= 4, article: 'Art. 6 - NGAP' });
  }
  if (actes.some((c) => c.article5ter)) {
    limites.push({ code: '5ter', nom: 'Surveillance post-hospitalisation', limite: '15 séances max', actuel: '1 séance', ok: true, article: 'Art. 5ter - NGAP' });
  }
  if (limites.length === 0) {
    limites.push({ code: '—', nom: 'Limites quotidiennes', limite: 'Aucune limite spécifique', actuel: 'Conforme', ok: true, article: '—' });
  }

  // ─── Étape 12 : Total général ───
  // Le déplacement (IFD hors BSI / IFI sous BSI, même montant 2,75 €) est déjà
  // compté une fois dans la majoration IFD. Le forfait BSA/BSB/BSC est déjà
  // présent dans les actes (sous-total). On n'ajoute donc rien de plus ici.
  const totalGeneral = sousTotalActesRounded + totalMajorations;
  const totalGeneralRounded = Math.round(totalGeneral * 100) / 100;

  // ─── Étape 13 : Score de risque ───
  let score = 100;
  const riskDetails: string[] = [];
  const actsWithZeroTaux = actes.filter((c) => c.tauxFacturation === 0 && !c.inclusDansForfait);
  if (actsWithZeroTaux.length > 0) {
    score -= 5;
    riskDetails.push(`${actsWithZeroTaux.length} acte(s) non facturé(s) — règle de cumul 100/50/0 (Art. 11B).`);
  }
  if (hasBSIContext && !hasForfait && !hasBSI) {
    score -= 15;
    riskDetails.push('ATTENTION : Contexte BSI détecté mais sans forfait renseigné. Vérifier la cotation en AMX.');
  }
  if (hasForfait && !hasBSI) {
    score -= 5;
    riskDetails.push('ℹ️ Forfait BSA/BSB/BSC : nécessite un BSI validé sur Amelipro.');
  }
  if (actesInjection > 3) { score -= 15; riskDetails.push(`ATTENTION : ${actesInjection} passages d'injection, limite quotidienne de 3.`); }
  if (actesPansement > 1) { score -= 10; riskDetails.push('ATTENTION : Plusieurs pansements dans la même journée.'); }
  if (contradictions.length > 0) {
    const nbCritiques = contradictions.filter((c) => c.type === 'error' && c.severite === 'critique').length;
    const nbErreurs = contradictions.filter((c) => c.type === 'error').length;
    const nbWarnings = contradictions.filter((c) => c.type === 'warning').length;
    score -= nbCritiques * 20;
    score -= (nbErreurs - nbCritiques) * 10;
    score -= nbWarnings * 5;
  }
  if (riskDetails.length === 0) riskDetails.push('Aucun point de risque identifié. La cotation respecte les règles NGAP 2026.');
  score = Math.max(0, Math.min(100, score));
  const niveau = score >= 85 ? 'Faible' : score >= 65 ? 'Modéré' : 'Élevé';
  const couleur = score >= 85 ? 'green' : score >= 65 ? 'amber' : 'red';

  // ─── Étape 14 : Avertissements ───
  const avertissements: string[] = [];
  avertissements.push('💡 Conservez l\'ordonnance et ce rapport de conformité pendant 2 ans (délai de prescription des contrôles CPAM).');
  if (allActsAreTelesoin) {
    avertissements.push('💻 Acte(s) de télésoin : pas de déplacement physique, donc pas d\'IFD ni d\'IK.');
  } else {
    avertissements.push('ℹ️ IFD (2,75 € par période visitée) incluse pour les passages physiques. IK kilométriques non calculées — dépendent de la distance réelle.');
  }

  const nb5bisActs = actes.filter((c) => (hasDiabete && (c.article5bis || isDiabeticEligibleAct(c.label))) || (!hasDiabete && c.article5bis)).length;

  if (hasForfait && !hasBSI) avertissements.push('⚠️ Forfaits BSA/BSB/BSC nécessitent un BSI validé. Sans BSI validé → facturer les actes techniques en AMI/AMX.');
  if (hasBSI) {
    avertissements.push('📋 BSI : à saisir sur Amelipro avec votre carte CPS.');
    avertissements.push('📋 BSI intermédiaire : limité à 2 par an, uniquement en cas d\'évolution clinique justifiée.');
  }
  if (hasBSIContext && !hasOnlyBSIActs) {
    avertissements.push('💡 Sous BSI : les actes techniques passent en AMX à 50%, sauf actes dérogatoires (AMX à taux plein) et actes Article 7 (AMI à taux plein).');
  }
  if (actsWithZeroTaux.length > 0) {
    avertissements.push('📐 Règle de cumul appliquée (Art. 11B) : 1er acte 100%, 2e 50%, suivants gratuits.');
  }
  const hasPerfCourte = actes.some((c) => normCode(c.code) === 'AMI 9' || normCode(c.code) === 'AMI 10');
  const hasPerfLongue = actes.some((c) => normCode(c.code) === 'AMI 14' || normCode(c.code) === 'AMI 15');
  if (hasPerfCourte && hasPerfLongue) {
    avertissements.push('💧 Perfusions (CIR-9/2025) : une perfusion courte indépendante se cumule à taux plein avec une perfusion longue. Les actes associés (AMI 4,1) sont plafonnés à 2 par passage.');
  }
  const hasPonctionVeineuse = actes.some((c) => c.jamaisDecote);
  const hasAnalgesieTopique = actes.some((c) => c.analgesieTopique);
  if (hasPonctionVeineuse) avertissements.push('💉 Ponction veineuse directe (AMI 1,5) : jamais décotée à 50%, reste à taux plein en 2e position.');
  if (hasAnalgesieTopique) avertissements.push('🧴 Analgésie topique (AMI 1,1) : cumulable à taux plein avec un pansement d\'ulcère ou d\'escarre.');
  const hasPansementComplexePerfusion = actes.some((c) => c.regleCumul === 'Pansement complexe + perfusion — cumul à taux plein');
  if (hasPansementComplexePerfusion) avertissements.push('🩹 Pansement complexe (AMI 4) : cumulable à taux plein avec une séance de perfusion (pas de décote Art. 11B).');
  const hasAMI6Valid = actes.some((c) => normCode(c.code) === 'AMI 6' && c.montant > 0);
  if (hasAMI6Valid) avertissements.push('⏱️ AMI 6 (' + formatAMI(6) + '/h) : heure supplémentaire de surveillance continue, durée totale limitée à 5 heures.');
  if (hasOnlyAMI4Perfusion) {
    avertissements.push('CIR-9/2025 — AMI 4 : pas droit aux frais de déplacement ni aux majorations nuit/dimanche.');
  }
  if (hasDiabete && nb5bisActs >= 2) {
    avertissements.push(`🩺 Article 5bis : patient diabétique insulino-traité — ${nb5bisActs} acte(s) lié(s) au diabète cumulé(s) à taux plein (100%) sans application de l'Article 11B.`);
  }
  if (hasDiabete) {
    avertissements.push('💡 Bon à savoir (Art. 5bis) : l\'article 5 bis ne s\'applique qu\'aux patients insulino-dépendants, diagnostiqués par le médecin traitant.');
  }
  if (hasPrado) {
    avertissements.push('🏥 PRADO (AMI 5,8) : cumul à taux plein avec un acte technique (Art. 11B dérogatoire). Fiche de suivi à transmettre au médecin traitant sous 48h.');
  }
  if (hasPostOp) {
    avertissements.push('🔪 Article 7 : soins postopératoires — les actes se cumulent à taux plein. Le drain de Redon (AMI 2,8) ne se cumule PAS avec le cathéter périnerveux (AMI 4,2).');
  }
  if (hasBSIContext && hasOnlyBSIActs && majorations.some((m) => m.applicable)) {
    avertissements.push('⚠️ Majorations non cumulables avec les forfaits BSI.');
  }
  if (actesInjection >= 2) avertissements.push('⚠️ Les passages multiples doivent être explicitement prescrits.');
  if (actesPansement > 1) avertissements.push('⚠️ Plusieurs pansements cotés : vérifier la prescription médicale explicite.');
  if (hasNuit2) avertissements.push('⚠️ Majoration nuit profonde (18,30 €) : intervention médicalement justifiée requise.');
  if (dimancheApplicable) avertissements.push('⚠️ Majoration dimanche/JF (8,50 €) : conservez le justificatif de la date.');
  if (nuitActive && hasDimanche) avertissements.push('⚠️ Nuit et dimanche/JF ne se cumulent pas : la majoration nuit prime.');
  avertissements.push('💡 Tarifs Métropole. DOM/Mayotte : AMI=3,30€, BSA=13,25€, BSB=18,55€, BSC=29,25€.');

  // ─── Étape 15 : Sources ───
  const sources: string[] = [
    'Article 11B NGAP — Actes de soins infirmiers et pansements — Règle de cumul 100/50/0',
    'Article 12 NGAP — Démarche de soins infirmiers (BSI)',
    'Article 13B NGAP — Prélèvements biologiques',
    'Article 14 NGAP — Majorations dimanches et jours fériés',
    'Article 15 NGAP — Majorations de nuit',
    'Article 5bis NGAP — Patient diabétique insulino-traité : cumul à taux plein',
    'Article 5ter NGAP — Surveillance post-hospitalisation (AMI 5,8, max 15 séances)',
    'PRADO — Programme de retour à domicile : AMI 5,8, cumul à taux plein (Art. 11B dérogatoire), non cumulable avec une autre surveillance clinique ni avec nuit/dimanche',
    'Article 6 NGAP — Dialyse péritonéale à domicile',
    'Article 7 NGAP — Soins postopératoires (RAAC)',
    'Article 10 NGAP — Surveillance de traitement (TA, limité à 15 jours)',
    'Article A12 Titre XVI — Actes dérogatoires : AMX à taux plein sous BSI',
    'Avenant n°6 — Convention nationale IDEL (BSI, MCI, MAU, IFD)',
    'Avenant n°11 — Convention nationale IDEL (BSI, forfaits dépendance)',
    'Avenant n°9 — Convention nationale IDEL — Télésoin (TMI, TLS, TLL, TLD)',
    'CIR-9/2025 — AMI 4 : organisation surveillance perfusion longue (hors pose/retrait)',
    'CIR-9/2025 — AMI 5 redéfini : arrêt définitif > 24h ou retrait Midline/Picc Line',
    'CIR-9/2025 — AMI 4,1 : actes associés aux perfusions longues',
    'CIR-9/2025 — AMI 9/10 inchangés : perfusion courte ≤ 1h sous surveillance',
    'CIR-9/2025 — AMI 6 : heure supplémentaire de surveillance continue',
    'NGAP — Perfusions : séances courtes + longues cumul à taux plein',
    'NGAP — Pansement complexe (AMI 4) + perfusion : cumul à taux plein',
  ];

  // ─── Étape 16 : Contradictions (règles métier transverses) ───
  let contradictionId = 0;

  // Forfait sans BSI validé → simple rappel (le forfait sélectionné implique un BSI)
  if (hasForfait && !hasBSI) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'info',
      titre: 'Forfait dépendance actif',
      description: `Le forfait ${forfait!.code} nécessite un BSI (Bilan de Soins Infirmiers) validé sur Amelipro.`,
      suggestion: 'Vérifiez que le BSI est bien saisi sur Amelipro avec votre carte CPS.',
      severite: 'faible',
      articles: ['BSI 2024', 'Art. 12 NGAP', 'Amelipro'],
    });
  }

  // Actes inclus dans le forfait (nursing, pansement simple, administration de traitement)
  const actesInclus = actes.filter((a) => a.inclusDansForfait);
  if (actesInclus.length > 0) {
    const labels = actesInclus.map((a) => a.label).join(', ');
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'error',
      titre: `Acte(s) inclus dans le forfait ${forfait!.code}`,
      description: `Vous avez sélectionné « ${labels} » alors qu'un forfait ${forfait!.code} est actif. Ces actes sont inclus dans le forfait et ne peuvent pas être facturés séparément.`,
      suggestion: `Supprimez ces actes. Le forfait ${forfait!.code} (${forfait!.montantJournalier.toFixed(2).replace('.', ',')} €/j) les couvre déjà.`,
      severite: 'critique',
      articles: ['BSI 2024', 'Avenant 8 — Forfaits dépendance'],
    });
  }

  // Actes AMI non dérogatoires sous BSI
  if (hasBSIContext) {
    const amiNonDerogatoires = actes.filter((c) => {
      if (c.categorie === 'Forfait dépendance' || c.categorie === 'Démarche de soins' || c.categorie === 'Soins infirmiers' || c.categorie === 'Télésoin' || c.coefficient === 0) return false;
      return !c.derogatoire && !c.article7 && c.code.startsWith('AMI');
    });
    if (amiNonDerogatoires.length > 0) {
      contradictionId++;
      contradictions.push({
        id: `ctr-${contradictionId}`,
        type: 'warning',
        titre: 'Actes en AMI au lieu d\'AMX sous BSI',
        description: `${amiNonDerogatoires.length} acte(s) non dérogatoire(s) en AMI dans un contexte BSI.`,
        suggestion: 'Convertissez ces actes en AMX à 50% du coefficient.',
        severite: 'eleve',
        articles: ['Art. A12 Titre XVI', 'BSI 2024'],
      });
    }
  }

  // Acte gratuit (3e+)
  if (actsWithZeroTaux.length > 0) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'warning',
      titre: 'Acte(s) non facturable(s) — règle de cumul',
      description: `${actsWithZeroTaux.length} acte(s) en 3e position ou au-delà dans la même séance.`,
      suggestion: 'Classez toujours les actes du coefficient le plus élevé au plus faible.',
      severite: 'modere',
      articles: ['Art. 11B NGAP'],
    });
  }

  // Pansements multiples
  if (actesPansement > 1) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'warning',
      titre: 'Pansements multiples dans la même journée',
      description: `${actesPansement} pansements détectés. Un seul par jour en règle générale, sauf prescription contraire.`,
      suggestion: 'Vérifiez la prescription médicale.',
      severite: 'eleve',
      articles: ['Art. 11B NGAP'],
    });
  }

  // Injections > 3 passages
  if (actesInjection > 3) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'warning',
      titre: 'Dépassement du nombre de passages d\'injection',
      description: `${actesInjection} passages d'injection, limite quotidienne de 3.`,
      suggestion: 'Vérifiez la prescription médicale.',
      severite: 'eleve',
      articles: ['Art. 11B NGAP'],
    });
  }

  // Bilan initial AMI 11 non cumulable avec un autre pansement
  const bilanInitialActs = actes.filter((c) => normCode(c.code) === 'AMI 11');
  if (bilanInitialActs.length > 0) {
    const passagesBilan = new Set(bilanInitialActs.map((c) => c.passage));
    let pansementDetecte = false;
    actes = actes.map((c) => {
      if (normCode(c.code) === 'AMI 11') return c;
      if (!passagesBilan.has(c.passage)) return c;
      if (c.categorie !== 'Pansements') return c;
      pansementDetecte = true;
      return { ...c, montant: 0, tauxFacturation: 0, justification: `${c.justification} — L'AMI 11 englobe déjà le pansement complexe du jour. Montant mis à 0 €.` };
    });
    if (pansementDetecte) {
      contradictionId++;
      contradictions.push({
        id: `ctr-${contradictionId}`,
        type: 'error',
        titre: 'Bilan initial AMI 11 non cumulable avec un autre pansement (même plaie)',
        description: 'L\'AMI 11 englobe déjà le soin de pansement complexe réalisé ce jour-là.',
        suggestion: 'Supprimez le pansement. L\'AMI 11 couvre l\'évaluation, la fiche descriptive, le projet de soins ET le pansement du jour.',
        severite: 'critique',
        articles: ['Art. 11B NGAP', 'Bilan initial pansements lourds (AMI 11)'],
      });
    }
    avertissements.push('💡 AMI 11 (' + formatAMI(11) + ') : facturable une seule fois par an et par patient. La MCI ne se cumule pas avec l\'AMI 11.');
  }

  // Télésoin : non-cumul
  const telesoinActs = actes.filter((c) => c.categorie === 'Télésoin');
  if (telesoinActs.length > 0) {
    const forfaitDependance = actes.find((c) => c.categorie === 'Forfait dépendance');
    if (forfaitDependance) {
      contradictionId++;
      contradictions.push({
        id: `ctr-${contradictionId}`,
        type: 'error',
        titre: 'Télésoin non cumulable avec un forfait BSI',
        description: 'Les actes de télésoin ne sont pas cumulables avec les forfaits BSI.',
        suggestion: 'Ne le facturez pas le même jour qu\'un forfait BSI.',
        severite: 'critique',
        articles: ['Avenant n°9', 'BSI 2024'],
      });
    }
    const telesoinCodes = new Set(telesoinActs.map((c) => c.code));
    if (telesoinCodes.size > 1) {
      contradictionId++;
      contradictions.push({
        id: `ctr-${contradictionId}`,
        type: 'warning',
        titre: 'Plusieurs actes de télésoin différents',
        description: 'Les actes de télésoin ne sont pas cumulables entre eux.',
        suggestion: 'Ne facturez qu\'un seul acte de télésoin par séance.',
        severite: 'eleve',
        articles: ['Avenant n°9'],
      });
    }
  }

  // Cohérence insuline/glycémie
  const rawKeywords: string[] = actes.map((a) => a.sourceKeyword || a.label).filter(Boolean);
  const hasDeuxiemeInsuline = rawKeywords.some(estDeuxiemeInsulineKw);
  const hasPremiereInsuline = rawKeywords.some(estInsulinePremiereKw);
  const hasInsulineAny = rawKeywords.some((kw) => kw.toLowerCase().includes('insuline'));
  const hasGlycemieAny = rawKeywords.some(estGlycemieKw);
  if (hasDeuxiemeInsuline && !hasPremiereInsuline) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'error',
      titre: '2e insuline sans 1re injection d\'insuline',
      description: 'La 2e insuline ne peut pas être cotée sans 1re injection d\'insuline le même jour.',
      suggestion: 'Cochez d\'abord la 1re injection d\'insuline.',
      severite: 'critique',
      articles: ['Art. 5bis NGAP', 'Avenant 6'],
    });
  }
  if (hasInsulineAny && !hasGlycemieAny) {
    contradictionId++;
    contradictions.push({
      id: `ctr-${contradictionId}`,
      type: 'warning',
      titre: 'Insuline sans contrôle de glycémie',
      description: 'La glycémie capillaire est le prérequis de sécurité de l\'injection d\'insuline.',
      suggestion: 'Confirmez qu\'une glycémie capillaire a été réalisée avant l\'injection.',
      severite: 'eleve',
      articles: ['Art. 5bis NGAP', 'Surveillance glycémique'],
    });
  }

  // PRADO non cumulable avec une surveillance clinique
  if (hasPrado) {
    const survCliniquePRADO = actes.filter((c) =>
      c.label.toLowerCase().includes('surveillance clinique') || c.label.toLowerCase().includes('hebdo')
    );
    if (survCliniquePRADO.length > 0) {
      contradictionId++;
      contradictions.push({
        id: `ctr-${contradictionId}`,
        type: 'warning',
        titre: 'PRADO non cumulable avec une séance hebdomadaire de surveillance clinique',
        description: 'L\'AMI 5,8 du PRADO ne se cumule pas avec une autre surveillance clinique.',
        suggestion: 'Le PRADO a été conservé à taux plein ; la seconde surveillance n\'a pas été cotée.',
        severite: 'eleve',
        articles: ['PRADO', 'Art. 5ter NGAP'],
      });
    }
  }

  // ─── Étape 17 : Résumé ───
  const categories = [...new Set(actes.map((c) => c.categorie))];
  const cumulInfo = actsWithZeroTaux.length > 0 ? ' — Cumul 100/50/0 appliqué' : '';
  const resumeAnalyse = `${actes.length} acte${actes.length > 1 ? 's' : ''} — ${categories.length} catégorie${categories.length > 1 ? 's' : ''}${cumulInfo}`;

  // Recalcul du sous-total final (après les ajustements AMI 11)
  const sousTotalFinal = actes.reduce((sum, c) => sum + c.montant, 0);
  const sousTotalActesFinal = Math.round(sousTotalFinal * 100) / 100;

  const totalGeneralFinal = sousTotalActesFinal + totalMajorations;
  const totalGeneralFinalRounded = Math.round(totalGeneralFinal * 100) / 100;

  return {
    actes,
    cumuls,
    majorations,
    limites,
    contradictions,
    total: {
      sousTotalActes: sousTotalActesFinal,
      totalMajorations,
      totalGeneral: totalGeneralFinalRounded,
    },
    risque: { niveau, score, couleur, details: riskDetails },
    sources,
    avertissements,
    resumeAnalyse,
    actesInjection,
    actesPansement,
    contexte: {
      hasBSI,
      hasForfait,
      hasDiabete,
      hasPrado,
      forfait,
      ifi: { montantUnitaire: MAJ_IFD, nbVisites: nbPassagesIFD, totalIFI: totalIFD },
    },
    explications,
  };
}

// ───────────────────────────────────────────────
// FUSION GLYCÉMIE → SURVEILLANCE DIABÉTIQUE (Art. 5bis)
// ───────────────────────────────────────────────
// La glycémie capillaire (dextro) réalisée dans le cadre de la surveillance
// d'un patient diabétique insulino-traité est ABSORBÉE par l'acte de
// surveillance glycémique (Art. 5bis, AMI 1) : elle ne génère jamais de ligne
// de cotation séparée. Si aucun acte de surveillance diabétique n'est déjà
// présent, il est créé automatiquement (glycémie + insuline → surveillance).
// ───────────────────────────────────────────────

function estGlycemieIsolee(a: ActeCalcule): boolean {
  if (a.article5bis) return false;
  const l = a.label.toLowerCase();
  return l.includes('glycém') || l.includes('glycem') || l.includes('dextro');
}

function estSurveillanceOuInsuline5bis(a: ActeCalcule): boolean {
  if (!a.article5bis) return false;
  const l = a.label.toLowerCase();
  if (l.includes('pansement')) return false;
  return l.includes('insuline') || l.includes('surveillance') || l.includes('diabét') || l.includes('diabet');
}

function estSurveillanceDiabetiqueExistante(a: ActeCalcule): boolean {
  if (!a.article5bis) return false;
  const l = a.label.toLowerCase();
  return l.includes('surveillance') || l.includes('diabét') || l.includes('diabet');
}

function fusionGlycemieSurveillance(
  actes: ActeCalcule[],
  hasDiabete: boolean,
): { actes: ActeCalcule[]; explications: string[] } {
  if (!hasDiabete) return { actes, explications: [] };

  const explications: string[] = [];
  const groups: Record<string, ActeCalcule[]> = {};
  actes.forEach((a) => {
    if (!groups[a.passage]) groups[a.passage] = [];
    groups[a.passage].push(a);
  });

  const sortie: ActeCalcule[] = [];

  Object.values(groups).forEach((acts) => {
    const survOuInsu = acts.filter(estSurveillanceOuInsuline5bis);
    const glycemies = acts.filter(estGlycemieIsolee);
    const survDiabExistantes = acts.filter(estSurveillanceDiabetiqueExistante);

    if (survOuInsu.length === 0 || glycemies.length === 0) {
      sortie.push(...acts);
      return;
    }

    // 1. Absorption de la glycémie isolée (dextro) — plus de ligne séparée
    const absorbedIds = new Set(glycemies.map((a) => a.id));
    sortie.push(...acts.filter((a) => !absorbedIds.has(a.id)));

    explications.push(
      `Fusion glycémie → surveillance diabétique (Art. 5bis) : ${glycemies.length} contrôle(s) glycémique(s) (dextro) absorbé(s) par la surveillance — pas de cotation séparée.`
    );

    // 2. Auto-génération de la surveillance diabétique si absente
    if (survDiabExistantes.length === 0) {
      const ref = survOuInsu[0] || glycemies[0];
      const montantSurv = Math.round(1 * BASE_AMI * 100) / 100;
      sortie.push({
        id: `surv-diab-${ref.passage}`,
        code: 'AMI 1',
        label: 'Surveillance patient diabétique insulino-traité (Art. 5bis)',
        coefficient: 1,
        baseAmount: BASE_AMI,
        base: formatMontant(BASE_AMI),
        montant: montantSurv,
        passage: ref.passage,
        periode: ref.periode,
        categorie: 'Surveillance',
        article: 'Art. 5bis - NGAP — Surveillance glycémique patient diabétique insulino-traité',
        justification: 'Acte de surveillance glycémique généré automatiquement (Art. 5bis) : le contrôle glycémique est intégré à cet acte.',
        tauxFacturation: 100,
        derogatoire: false,
        article5bis: true,
        article7: false,
        article5ter: false,
        article6: false,
        noMCI: false,
        jamaisDecote: false,
        analgesieTopique: false,
        cumulableAvecAnalgesie: false,
        inclusDansForfait: false,
        exceptionAppliquee: 'SURVEILLANCE DIABÉTIQUE 5BIS',
        regleCumul: null,
        sourceKeyword: 'surveillance diabetique',
      });
      explications.push(
        'Surveillance diabétique (AMI 1, Art. 5bis) créée automatiquement : glycémie + insuline dans le même passage.'
      );
    }
  });

  return { actes: sortie, explications };
}

// ───────────────────────────────────────────────
// RÈGLES PRADO (AMI 5,8 — Art. 5ter)
// ───────────────────────────────────────────────
function appliquerReglesPrado(actes: ActeCalcule[]): ActeCalcule[] {
  const isPrado = (c: ActeCalcule): boolean => c.article5ter;
  const isSurveillanceClinique = (c: ActeCalcule): boolean => {
    if (isPrado(c)) return false;
    const l = c.label.toLowerCase();
    return l.includes('surveillance clinique') || l.includes('hebdo');
  };
  const isHorsCumul = (c: ActeCalcule): boolean =>
    c.categorie === 'Forfait dépendance' || c.categorie === 'Démarche de soins' ||
    c.categorie === 'Télésoin' || c.categorie === 'Soins infirmiers' || c.coefficient === 0;

  const baseJustif = (c: ActeCalcule): string => `${c.label}. ${c.code} coefficient ${c.coefficient}.`;

  const groups: Record<string, ActeCalcule[]> = {};
  actes.forEach((c) => {
    if (!groups[c.passage]) groups[c.passage] = [];
    groups[c.passage].push(c);
  });

  const sortie: ActeCalcule[] = [];

  Object.values(groups).forEach((acts) => {
    const pradoActs = acts.filter(isPrado);
    if (pradoActs.length === 0) {
      sortie.push(...acts);
      return;
    }

    const techActs = acts
      .filter((c) => !isPrado(c) && !isSurveillanceClinique(c) && !isHorsCumul(c))
      .sort((a, b) => b.coefficient - a.coefficient);

    sortie.push(...acts.map((c) => {
      if (isPrado(c)) {
        return {
          ...c,
          code: c.code.replace(/\s*\[.*\]\s*$/, ''),
          montant: Math.round(c.coefficient * c.baseAmount * 100) / 100,
          tauxFacturation: 100,
          regleCumul: 'PRADO — acte dérogatoire toujours à taux plein',
          justification: `${baseJustif(c)} — PRADO (Art. 5ter) : acte dérogatoire toujours à taux plein (jamais décoté à 50 %).`,
        };
      }
      if (isSurveillanceClinique(c)) {
        return {
          ...c,
          code: `${c.code.replace(/\s*\[.*\]\s*$/, '')} [NON COTÉ — PRADO]`,
          montant: 0,
          tauxFacturation: 0,
          regleCumul: 'Non-cumul PRADO — surveillance redondante non cotée',
          justification: `${baseJustif(c)} — Non-cumul PRADO : ne se cumule pas avec une autre surveillance clinique.`,
        };
      }
      if (techActs.some((o) => o.id === c.id)) {
        const idx = techActs.findIndex((o) => o.id === c.id);
        if (idx === 0) {
          return {
            ...c,
            code: c.code.replace(/\s*\[.*\]\s*$/, ''),
            montant: Math.round(c.coefficient * c.baseAmount * 100) / 100,
            tauxFacturation: 100,
            regleCumul: 'Cumul PRADO à taux plein (Art. 11B dérogatoire)',
            justification: `${baseJustif(c)} — Cumul PRADO à taux plein : l'acte technique se cumule à 100 % avec le PRADO, sans décote de moitié.`,
          };
        }
        if (idx === 1) {
          return {
            ...c,
            montant: Math.round(c.coefficient * c.baseAmount * 0.5 * 100) / 100,
            tauxFacturation: 50,
            regleCumul: '2e acte technique — 50% (Art. 11B)',
            justification: `${baseJustif(c)} — 2e acte technique de la séance → 50 % (Art. 11B).`,
          };
        }
        return {
          ...c,
          montant: 0,
          tauxFacturation: 0,
          regleCumul: '3e acte technique — gratuit (Art. 11B)',
          justification: `${baseJustif(c)} — 3e acte technique et suivants → gratuit (Art. 11B).`,
        };
      }
      return c;
    }));
  });

  return sortie;
}